import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  EyeIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  LinkIcon,
  CameraIcon,
  RocketLaunchIcon,
  XMarkIcon,
  HandRaisedIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import {
  fetchChecklistProgress,
  markChecklistItem,
  fetchCreatorStats,
  fetchAdminSuggestions,
  fetchBioProofCount,
  uploadBioProof,
  hasCustomizedLink,
  hasSponsorEnabled,
  hasSentChatMessage,
  fetchRecentViewCount,
  CreatorStats,
  AdminSuggestion,
} from '../services/creatorDashboard';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';

interface CreatorHubDashboardProps {
  talentProfile: { id: string; username?: string };
  onNavigate: (view: string) => void;
}

const CHECKLIST_ITEMS = [
  {
    key: 'customize_link',
    label: 'Finish customizing your ShoutOut Fans link',
    description: 'Add links, themes, and content to your bio page.',
    actionLabel: 'Customize',
    actionType: 'navigate' as const,
    actionTarget: 'customize',
  },
  {
    key: 'earn_50_bio',
    label: 'Earn $30 by using your link exclusively in your bio on 2 platforms',
    description: 'Upload 2 screenshots of your profile showing the link.',
    actionLabel: 'Done!',
    actionType: 'done' as const,
    actionTarget: '',
  },
  {
    key: 'enable_sponsors',
    label: 'Turn on Pre-Paid Sponsors to earn $600-$1,200 per 1000 clicks',
    description: 'Enable sponsor ads on your bio page.',
    actionLabel: 'Enable',
    actionType: 'navigate' as const,
    actionTarget: 'customize',
  },
  {
    key: 'chat_intro',
    label: 'Introduce yourself in the #creators chat to unlock the next steps',
    description: 'Say hello so other creators know who you are.',
    actionLabel: 'Okay!',
    actionType: 'navigate' as const,
    actionTarget: 'chat:creators',
  },
  {
    key: 'complete_course',
    label: 'Earn $20 by answering a few questions',
    description: 'Quick survey that helps us improve ShoutOut for you.',
    actionLabel: "Let's do it!",
    actionType: 'navigate' as const,
    actionTarget: 'course',
  },
];

const SOCIAL_NETWORKS = [
  { id: 'youtube', label: 'YouTube' },
  { id: 'rumble', label: 'Rumble' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'twitter', label: 'Twitter / X' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'truth_social', label: 'Truth Social' },
  { id: 'telegram', label: 'Telegram' },
  { id: 'gettr', label: 'GETTR' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'podcast', label: 'Podcast' },
  { id: 'snapchat', label: 'Snapchat' },
  { id: 'other', label: 'Other' },
];

const ESTABLISHED_CREATOR_HIDDEN_ITEMS = [
  'earn_50_bio',
  'chat_intro',
  'complete_course',
];

// Summary line to show next to each completed checklist item after the whole
// checklist is done. Helps creators see exactly which steps earned them money
// so the total on the Earnings card isn't a mystery.
const CHECKLIST_REWARDS: Record<string, { amount: number | null; label: string }> = {
  customize_link: { amount: null, label: 'Bio customized' },
  earn_50_bio: { amount: 30, label: '$30 · Bio proof (pending approval)' },
  enable_sponsors: { amount: null, label: 'Pre-paid sponsors enabled' },
  chat_intro: { amount: null, label: 'Intro sent — next steps unlocked' },
  complete_course: { amount: 20, label: '$20 · Onboarding course' },
  story_promo: { amount: null, label: 'Promo requested' },};

const CreatorHubDashboard: React.FC<CreatorHubDashboardProps> = ({ talentProfile, onNavigate }) => {
  const [stats, setStats] = useState<CreatorStats>({ totalEarnings: 0, pendingApprovalEarnings: 0, totalViews: 0, avgCtr: 0 });
  const [suggestions, setSuggestions] = useState<AdminSuggestion[]>([]);
  const [checklistCompleted, setChecklistCompleted] = useState<Record<string, boolean>>({});
  const [animatingItem, setAnimatingItem] = useState<string | null>(null);
  const [showProofUpload, setShowProofUpload] = useState(false);
  const [proofCount, setProofCount] = useState(0);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [followerInputs, setFollowerInputs] = useState<Record<string, number>>({});
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [chatBannerDismissed, setChatBannerDismissed] = useState(() => localStorage.getItem('chat_banner_dismissed') === 'true');
  const [creatorCallRsvped, setCreatorCallRsvped] = useState(() => localStorage.getItem('creators_call_0520_rsvp') === 'true');
  const [rsvping, setRsvping] = useState(false);
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isEstablishedCreator, setIsEstablishedCreator] = useState(false);
  const [isPartner, setIsPartner] = useState<boolean | null>(null);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [agreedToPartnerTerms, setAgreedToPartnerTerms] = useState(false);
  const [enrollingPartner, setEnrollingPartner] = useState(false);
  const [first10CouponCode, setFirst10CouponCode] = useState<string | null>(null);
  const [first10OrderCount, setFirst10OrderCount] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bioUrl = `shoutout.fans/${talentProfile.username || talentProfile.id}`;

  const loadDashboardData = useCallback(async () => {
    try {
      const [progressMap, creatorStats, adminSuggestions, bioProofCt, linkCustomized, sponsorsOn, alreadyChatted, recentViews] = await Promise.all([
        fetchChecklistProgress(talentProfile.id),
        fetchCreatorStats(talentProfile.id),
        fetchAdminSuggestions(talentProfile.id),
        fetchBioProofCount(talentProfile.id),
        hasCustomizedLink(talentProfile.id),
        hasSponsorEnabled(talentProfile.id),
        hasSentChatMessage(talentProfile.id),
        fetchRecentViewCount(talentProfile.id, 30),
      ]);

      const established = recentViews > 300;
      setIsEstablishedCreator(established);

      const completed: Record<string, boolean> = {};
      Object.keys(progressMap).forEach(key => { completed[key] = true; });
      if (linkCustomized) completed['customize_link'] = true;
      if (sponsorsOn) completed['enable_sponsors'] = true;
      if (alreadyChatted) completed['chat_intro'] = true;
      // Mark earn_50_bio complete as soon as 2 screenshots are uploaded.
      // Actual $50 payout still gates on admin approval, but the checklist
      // shouldn't feel stuck after the creator has uploaded both proofs.
      if (bioProofCt >= 2) completed['earn_50_bio'] = true;

      setChecklistCompleted(completed);
      setStats(creatorStats);
      setSuggestions(adminSuggestions);
      setProofCount(bioProofCt);

      const visibleItems = established
        ? CHECKLIST_ITEMS.filter(i => !ESTABLISHED_CREATOR_HIDDEN_ITEMS.includes(i.key))
        : CHECKLIST_ITEMS;
      setAllDone(visibleItems.every(item => completed[item.key]));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [talentProfile.id]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Load first-10-orders coupon and order count
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [{ data: code }, { count }] = await Promise.all([
          supabase.rpc('get_or_create_first10_coupon', { p_talent_id: talentProfile.id }),
          supabase
            .from('orders')
            .select('id', { count: 'exact', head: true })
            .eq('talent_id', talentProfile.id)
            .in('status', ['completed']),
        ]);
        if (cancelled) return;
        if (code) setFirst10CouponCode(code as string);
        setFirst10OrderCount(count ?? 0);
      } catch {
        // non-critical; silently ignore
      }
    };
    load();
    return () => { cancelled = true; };
  }, [talentProfile.id]);

  // Real-time subscriptions: re-fetch earnings when payouts or sponsor clicks change
  useEffect(() => {
    const channel = supabase
      .channel(`creator-earnings-${talentProfile.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payouts', filter: `talent_id=eq.${talentProfile.id}` },
        () => loadDashboardData()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'sponsor_clicks', filter: `talent_id=eq.${talentProfile.id}` },
        () => loadDashboardData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [talentProfile.id, loadDashboardData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_partner_for_talent', { p_talent_id: talentProfile.id });
        if (cancelled) return;
        if (error) { setIsPartner(false); return; }
        setIsPartner(!!(data && (data as any).is_active));
      } catch {
        if (!cancelled) setIsPartner(false);
      }
    })();
    return () => { cancelled = true; };
  }, [talentProfile.id]);

  const handleEnrollAsPartner = useCallback(async () => {
    if (!agreedToPartnerTerms || enrollingPartner) return;
    if (!talentProfile?.id) {
      toast.error('Missing talent profile');
      return;
    }
    setEnrollingPartner(true);
    try {
      const { error } = await supabase.rpc('self_enroll_as_partner', {
        p_talent_id: talentProfile.id,
        p_agreement_version: 'v1',
      });
      if (error) throw error;
      setIsPartner(true);
      setShowPartnerModal(false);
      setAgreedToPartnerTerms(false);
      window.dispatchEvent(new CustomEvent('partner-enrolled'));
      toast.success('You are now a ShoutOut partner! Check the Partner tab.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to activate partner account';
      toast.error(msg);
    } finally {
      setEnrollingPartner(false);
    }
  }, [agreedToPartnerTerms, enrollingPartner, talentProfile?.id]);

  // Listen for the creator's first chat message to auto-mark chat_intro done
  useEffect(() => {
    if (checklistCompleted['chat_intro'] || isEstablishedCreator) return;
    const sub = supabase
      .channel(`checklist-chat-${talentProfile.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `sender_talent_id=eq.${talentProfile.id}`,
      }, () => {
        setAnimatingItem('chat_intro');
        markChecklistItem(talentProfile.id, 'chat_intro').catch(() => {});
        setTimeout(() => {
          setChecklistCompleted(prev => {
            const next: Record<string, boolean> = { ...prev, chat_intro: true };
            const visible = CHECKLIST_ITEMS.filter(i => !ESTABLISHED_CREATOR_HIDDEN_ITEMS.includes(i.key));
            if (visible.every(i => next[i.key])) setAllDone(true);
            return next;
          });
          setAnimatingItem(null);
          // Re-fetch stats so the $20 shows up even if the realtime subscription missed the payout insert.
          setTimeout(() => { loadDashboardData(); }, 1500);
        }, 800);
        toast.success('Chat intro complete! $20 earned!');
      })
      .subscribe();
    return () => { supabase.removeChannel(sub); };
  }, [talentProfile.id, checklistCompleted, isEstablishedCreator]);

  // Close calendar dropdown on outside click
  useEffect(() => {
    if (!showCalendarDropdown) return;
    const close = () => setShowCalendarDropdown(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showCalendarDropdown]);

  const handleChecklistAction = async (item: typeof CHECKLIST_ITEMS[0]) => {
    if (checklistCompleted[item.key]) return;

    if (item.actionType === 'navigate') {
      onNavigate(item.actionTarget);
      return;
    }

    if (item.key === 'earn_50_bio') {
      setShowProofUpload(true);
      return;
    }

    try {
      await markChecklistItem(talentProfile.id, item.key);
      setAnimatingItem(item.key);
      setTimeout(() => {
        setChecklistCompleted(prev => ({ ...prev, [item.key]: true }));
        setAnimatingItem(null);
        const newCompleted = { ...checklistCompleted, [item.key]: true };
        if (visibleChecklistItems.every(i => newCompleted[i.key])) {
          setAllDone(true);
        }
      }, 800);
      // Re-fetch stats so newly-awarded checklist earnings show up immediately
      // (DB triggers create the payout row after markChecklistItem; give them a beat).
      setTimeout(() => { loadDashboardData(); }, 1500);
      toast.success('Marked as done!');
    } catch (error) {
      console.error('Error marking checklist item:', error);
      toast.error('Failed to update');
    }
  };

  const handleProofUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingProof(true);
    try {
      await uploadBioProof(talentProfile.id, file, `platform_${proofCount + 1}`);
      const newCount = proofCount + 1;
      setProofCount(newCount);
      toast.success(`Screenshot ${newCount}/2 uploaded!`);

      if (newCount >= 2) {
        setShowProofUpload(false);
        toast.success('Both screenshots uploaded! Awaiting admin approval for your $30 payout.');
        // Persist checklist completion so the step doesn't re-appear on reload.
        try {
          await markChecklistItem(talentProfile.id, 'earn_50_bio', { proof_count: newCount });
          setChecklistCompleted(prev => ({ ...prev, earn_50_bio: true }));
          // Save follower counts if the creator entered any
          const cleanedFollowers: Record<string, number> = {};
          Object.entries(followerInputs).forEach(([k, v]) => { if (v > 0) cleanedFollowers[k] = v; });
          if (Object.keys(cleanedFollowers).length > 0) {
            await supabase
              .from('talent_profiles')
              .update({ follower_counts: cleanedFollowers })
              .eq('id', talentProfile.id);
          }
          setTimeout(() => { loadDashboardData(); }, 1500);
        } catch (e) {
          console.error('Error marking earn_50_bio complete:', e);
        }
      }
    } catch (error) {
      console.error('Error uploading proof:', error);
      toast.error('Failed to upload screenshot');
    } finally {
      setUploadingProof(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`https://${bioUrl}`);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const visibleChecklistItems = isEstablishedCreator
    ? CHECKLIST_ITEMS.filter(i => !ESTABLISHED_CREATOR_HIDDEN_ITEMS.includes(i.key))
    : CHECKLIST_ITEMS;
  const completedCount = visibleChecklistItems.filter(i => checklistCompleted[i.key]).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const handleCreatorCallRsvp = async () => {
    if (rsvping || creatorCallRsvped) return;
    setRsvping(true);
    try {
      await supabase.from('creator_event_rsvps').upsert(
        { talent_id: talentProfile.id, event_key: 'creators_call_0520_2026' },
        { onConflict: 'talent_id,event_key' }
      );
      setCreatorCallRsvped(true);
      localStorage.setItem('creators_call_0520_rsvp', 'true');
      toast.success("You're in! See you on 5/20 🎉");
    } catch {
      toast.error('Something went wrong — please try again.');
    } finally {
      setRsvping(false);
    }
  };

  // May 20, 2026 at 8:00 PM Eastern Time (New York) = 8PM EDT = 2026-05-21T00:00:00Z
  // All calendar links use explicit UTC or IANA timezone so the event always
  // shows as 8PM Eastern regardless of the viewer's local timezone.
  const CAL_START_UTC = '20260521T000000Z'; // 8PM EDT (UTC-4)
  const CAL_END_UTC   = '20260521T010000Z'; // 9PM EDT (UTC-4)
  const CAL_TITLE    = encodeURIComponent('ShoutOut Creator Call');
  const CAL_DESC     = encodeURIComponent("Join our first Creator's Call with ShoutOut's Founders!\nVideo call link: https://meet.google.com/tum-eork-kim");
  const CAL_LOCATION = encodeURIComponent('https://meet.google.com/tum-eork-kim');

  const calendarLinks: { label: string; icon: string; url: string; download?: boolean }[] = [
    {
      label: 'Google Calendar',
      icon: '🗓️',
      // Google accepts UTC (Z) timestamps — always renders as 8PM Eastern
      url: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${CAL_TITLE}&dates=${CAL_START_UTC}/${CAL_END_UTC}&details=${CAL_DESC}&location=${CAL_LOCATION}&ctz=America%2FNew_York`,
    },
    {
      label: 'Apple / iCal',
      icon: '🍎',
      url: (() => {
        // Use TZID=America/New_York so Apple Calendar shows 8PM Eastern
        // regardless of where the creator is located.
        const ics = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'PRODID:-//ShoutOut//Creator Call//EN',
          'CALSCALE:GREGORIAN',
          'METHOD:PUBLISH',
          'BEGIN:VTIMEZONE',
          'TZID:America/New_York',
          'BEGIN:DAYLIGHT',
          'TZOFFSETFROM:-0500',
          'TZOFFSETTO:-0400',
          'TZNAME:EDT',
          'DTSTART:19700308T020000',
          'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU',
          'END:DAYLIGHT',
          'BEGIN:STANDARD',
          'TZOFFSETFROM:-0400',
          'TZOFFSETTO:-0500',
          'TZNAME:EST',
          'DTSTART:19701101T020000',
          'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU',
          'END:STANDARD',
          'END:VTIMEZONE',
          'BEGIN:VEVENT',
          'DTSTART;TZID=America/New_York:20260520T200000',
          'DTEND;TZID=America/New_York:20260520T210000',
          'SUMMARY:ShoutOut Creator Call',
          "DESCRIPTION:Join our first Creator's Call with ShoutOut's Founders!\\n\\nVideo call link: https://meet.google.com/tum-eork-kim",
          'LOCATION:https://meet.google.com/tum-eork-kim',
          'END:VEVENT',
          'END:VCALENDAR',
        ].join('\r\n');
        return URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
      })(),
      download: true,
    },
    {
      label: 'Outlook',
      icon: '📧',
      // Explicit -04:00 offset (EDT) anchors the time to Eastern regardless of viewer locale
      url: `https://outlook.live.com/calendar/0/action/compose?subject=${CAL_TITLE}&startdt=2026-05-20T20:00:00-04:00&enddt=2026-05-20T21:00:00-04:00&body=${CAL_DESC}&location=${CAL_LOCATION}`,
    },
    {
      label: 'Yahoo Calendar',
      icon: '🟣',
      url: `https://calendar.yahoo.com/?v=60&title=${CAL_TITLE}&st=${CAL_START_UTC}&et=${CAL_END_UTC}&desc=${CAL_DESC}&in_loc=${CAL_LOCATION}`,
    },
  ];

  const handleCalendarOption = (link: typeof calendarLinks[0]) => {
    if (link.download) {
      const a = document.createElement('a');
      a.href = link.url;
      a.download = 'shoutout-creator-call.ics';
      a.click();
    } else {
      window.open(link.url, '_blank');
    }
    setShowCalendarDropdown(false);
  };

  const CALL_AVATARS = [
    { name: 'Hayley', src: 'https://shoutout-assets.s3.us-central-1.wasabisys.com/talent-avatars/1762531665077-ks9vd57siig.jpeg' },
    { name: 'Shawn',  src: 'https://s3.us-central-1.wasabisys.com/shoutout-assets/avatars/temp-795c2aba-4e57-416d-822a-a029eea49d5d-1761753701165.jpg' },
    { name: 'Isabella', src: 'https://utafetamgwukkbrlezev.supabase.co/storage/v1/object/public/platform-assets/talent-avatars/1771892259328-3oezv3sd1r8.jpg' },
    { name: 'Greg',   src: 'https://utafetamgwukkbrlezev.supabase.co/storage/v1/object/public/platform-assets/talent-avatars/1768497829090-55ocx1yohij.jpeg' },
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">Creator Hub</h2>
        <button
          onClick={copyLink}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl text-sm font-semibold text-white transition-colors"
        >
          {copied ? (
            <CheckCircleIcon className="h-4 w-4 text-green-400" />
          ) : (
            <LinkIcon className="h-4 w-4 text-blue-400" />
          )}
          {copied ? 'Copied!' : 'Copy My Link'}
        </button>
      </div>

      {/* Creator's Call Banner */}
      <div className="relative bg-gradient-to-r from-purple-900/70 via-indigo-900/70 to-blue-900/70 border border-purple-500/40 rounded-2xl px-5 py-4 overflow-visible">
        {/* Gradient overlay — isolated overflow-hidden so the dropdown can escape */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(139,92,246,0.15),transparent_60%)]" />
        </div>
        <div className="relative flex items-center gap-4 flex-wrap sm:flex-nowrap z-10">
          {/* Overlapping avatars */}
          <div className="flex-shrink-0 flex items-center">
            {CALL_AVATARS.map((a, i) => (
              <img
                key={a.name}
                src={a.src}
                alt={a.name}
                title={a.name}
                className="w-9 h-9 rounded-full border-2 border-indigo-900 object-cover"
                style={{ marginLeft: i === 0 ? 0 : '-10px', zIndex: i }}
              />
            ))}
          </div>
          {/* Text */}
          <p className="text-white text-sm font-medium flex-1 leading-snug">
            {creatorCallRsvped ? (
              <>You RSVP'd to our first <span className="font-bold text-purple-200">Creator's Call on 5/20</span></>
            ) : (
              <>Join our first <span className="font-bold text-purple-200">Creator's Call on 5/20</span> with ShoutOut's Founders</>
            )}
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-white/15 text-purple-200 whitespace-nowrap align-middle">
              8PM EST
            </span>
          </p>
          {/* RSVP / Calendar button */}
          {creatorCallRsvped ? (
            <div className="relative flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); setShowCalendarDropdown(!showCalendarDropdown); }}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-white text-indigo-900 text-sm font-bold rounded-lg hover:bg-purple-100 transition-colors whitespace-nowrap"
              >
                <CalendarDaysIcon className="w-4 h-4" />
                Add to calendar
              </button>
              {showCalendarDropdown && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-gray-900 border border-white/20 rounded-xl shadow-2xl overflow-hidden z-[9999]">
                  {calendarLinks.map((link) => (
                    <button
                      key={link.label}
                      onClick={(e) => { e.stopPropagation(); handleCalendarOption(link); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-white/10 transition-colors text-left"
                    >
                      <span>{link.icon}</span>
                      {link.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={handleCreatorCallRsvp}
              disabled={rsvping}
              className="flex-shrink-0 px-4 py-1.5 bg-white text-indigo-900 text-sm font-bold rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-60"
            >
              {rsvping ? '...' : 'RSVP'}
            </button>
          )}
        </div>
      </div>

      {/* Checklist Section — always visible so completed creators can see the
          items (and $ amounts) they earned; when everything's done we flip
          the header to a celebratory "You earned $X" summary. */}
      {(() => {
        const checklistEarnings = visibleChecklistItems.reduce((sum, item) => {
          if (!checklistCompleted[item.key]) return sum;
          return sum + (CHECKLIST_REWARDS[item.key]?.amount || 0);
        }, 0);
        return (
        <div className="border border-white/20 rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RocketLaunchIcon className={`h-6 w-6 ${allDone ? 'text-green-400' : 'text-amber-400'}`} />
              <h2 className="text-lg font-bold text-white">
                {allDone
                  ? (checklistEarnings > 0
                    ? `Setup complete — you earned $${checklistEarnings.toFixed(2)}`
                    : 'Setup complete')
                  : 'Get setup and start monetizing'}
              </h2>
            </div>
            <span className="text-sm text-gray-400">{completedCount}/{visibleChecklistItems.length}</span>
          </div>

          <div className="max-h-[400px] overflow-y-auto overscroll-contain divide-y divide-white/10" style={{ WebkitOverflowScrolling: 'touch' }}>
            {visibleChecklistItems.map((item) => {
              const isDone = checklistCompleted[item.key];
              const isAnimating = animatingItem === item.key;
              const isLocked = !isDone && !isEstablishedCreator
                && (item.key === 'earn_50_bio' || item.key === 'complete_course')
                && !checklistCompleted['chat_intro'];

              return (
                <div
                  key={item.key}
                  className={`px-5 py-4 flex items-start gap-4 transition-all duration-500 ${
                    isAnimating ? 'bg-green-500/20 scale-[0.98]' : isDone ? 'opacity-60' : isLocked ? 'opacity-40' : ''
                  }`}
                >
                  <div className={`flex-shrink-0 mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${
                    isDone || isAnimating
                      ? 'border-green-500 bg-green-500'
                      : isLocked ? 'border-gray-600 bg-gray-800' : 'border-gray-500'
                  }`}>
                    {(isDone || isAnimating) && (
                      <CheckIcon className="h-4 w-4 text-white animate-bounce" />
                    )}
                    {isLocked && (
                      <svg className="h-3 w-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${isDone ? 'text-gray-500 line-through' : isLocked ? 'text-gray-500' : 'text-white'}`}>
                      {item.label}
                    </p>
                    {isLocked && (
                      <p className="text-xs text-gray-600 mt-0.5">Complete the chat intro first to unlock</p>
                    )}
                    {item.description && !isDone && !isLocked && (
                      <p className="text-sm text-gray-400 mt-0.5">{item.description}</p>
                    )}
                    {isDone && CHECKLIST_REWARDS[item.key] && (
                      <p className="text-sm text-green-400 mt-0.5">{CHECKLIST_REWARDS[item.key].label}</p>
                    )}
                  </div>

                  {!isDone && !isLocked && (
                    <button
                      onClick={() => handleChecklistAction(item)}
                      className="flex-shrink-0 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      {item.actionLabel}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}


      {/* Get your first ShoutOut orders — hidden once creator has 10+ completed orders */}
      {first10OrderCount !== null && first10OrderCount < 10 && first10CouponCode && (() => {
        const couponLink = `https://shoutout.fans/${talentProfile.username || talentProfile.id}?coupon=${first10CouponCode}`;
        const copyFirst10Link = () => {
          navigator.clipboard.writeText(couponLink);
          toast.success('Coupon link copied!');
        };
        return (
          <div className="border border-amber-500/30 rounded-2xl bg-amber-500/5 px-5 py-5 space-y-4">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <span className="text-lg">🎉</span> Get your first ShoutOut orders
              </h3>
              <p className="text-sm text-gray-300 mt-1 leading-relaxed">
                To jumpstart your video orders, post this link to your stories a few times a week to give your first{' '}
                <span className="font-semibold text-amber-300">10 customers 50% off</span> — we eat the coupon difference for you!
              </p>
            </div>

            {/* Copy link */}
            <button
              onClick={copyFirst10Link}
              className="w-full flex items-center justify-between gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 transition-colors group"
            >
              <div className="flex items-center gap-2 min-w-0">
                <LinkIcon className="h-4 w-4 text-amber-400 flex-shrink-0" />
                <span className="text-sm text-gray-300 truncate font-mono">{couponLink.replace('https://', '')}</span>
              </div>
              <span className="flex-shrink-0 text-xs font-semibold text-amber-300 group-hover:text-amber-200 whitespace-nowrap">
                Click to copy
              </span>
            </button>

            {/* Orders used counter */}
            <p className="text-xs text-gray-400">
              {first10OrderCount} / 10 orders used · {10 - first10OrderCount} remaining
            </p>
          </div>
        );
      })()}

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1 border border-white/20 rounded-2xl bg-white/5 px-5 py-4">
          <div className="flex items-center gap-2 mb-2">
            <CurrencyDollarIcon className="h-5 w-5 text-green-400" />
            <span className="text-sm text-gray-400">Total Earnings</span>
          </div>
          <p className="text-3xl font-bold text-white">${stats.totalEarnings.toFixed(2)}</p>
          {stats.pendingApprovalEarnings > 0 && (
            <p className="text-xs text-amber-300 mt-1">
              Includes ${stats.pendingApprovalEarnings.toFixed(2)} pending admin approval
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 col-span-2 sm:col-span-1">
          <div className="border border-white/20 rounded-2xl bg-white/5 px-4 py-4">
            <div className="flex items-center gap-1.5 mb-2">
              <EyeIcon className="h-4 w-4 text-blue-400" />
              <span className="text-xs text-gray-400">Views</span>
            </div>
            <p className="text-xl font-bold text-white">{stats.totalViews.toLocaleString()}</p>
          </div>
          <div className="border border-white/20 rounded-2xl bg-white/5 px-4 py-4">
            <div className="flex items-center gap-1.5 mb-2">
              <ChartBarIcon className="h-4 w-4 text-purple-400" />
              <span className="text-xs text-gray-400">Avg CTR</span>
            </div>
            <p className="text-xl font-bold text-white">{stats.avgCtr.toFixed(1)}%</p>
          </div>
        </div>
      </div>

      {/* Referral network card */}
      {isPartner === false && (
        <div className="border border-indigo-400/30 rounded-2xl bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 p-5">
          <div className="flex items-center gap-2 mb-2">
            <HandRaisedIcon className="h-5 w-5 text-indigo-300" />
            <h3 className="font-semibold text-white">Want to join our referral network?</h3>
          </div>
          <p className="text-sm text-indigo-100/90 mb-2">
            Make $50 &amp; give $50 when the referred creator uses ShoutOut Fans.
          </p>
          <p className="text-sm text-indigo-100/70 mb-4">
            We're building the largest free-speech creator community on the planet. With an
            insatiable drive to help creators in the space monetize better without fear of
            cancel culture.
          </p>
          <button
            onClick={() => setShowPartnerModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Become a partner
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Partner disclosure modal */}
      {showPartnerModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/20 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">ShoutOut Partner Agreement</h3>
              <button
                onClick={() => { setShowPartnerModal(false); setAgreedToPartnerTerms(false); }}
                className="text-gray-400 hover:text-white"
                aria-label="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-3 text-sm text-gray-300 mb-4">
              <p>
                By joining the ShoutOut Partner Program you agree to the following terms:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  You earn <span className="text-white font-medium">$50</span> when a creator you
                  refer adds their ShoutOut Fans link to their primary platform(s) exclusively
                  and it replaces their existing link-in-bio.
                </li>
                <li>
                  The referred creator also receives <span className="text-white font-medium">$50</span>,
                  paid out per ShoutOut's standard payout schedule once their account is set up.
                </li>
                <li>
                  Referrals must be attributed through your unique referral link. Self-referrals,
                  fraudulent signups, or incentivized signups that violate ShoutOut's terms are
                  not eligible for payout.
                </li>
                <li>
                  Payouts are made to a connected payout account (e.g. Moov) and may require
                  identity verification before funds can be released.
                </li>
                <li>
                  ShoutOut may modify, pause, or end the Partner Program or individual
                  partnerships at any time, and reserves the right to withhold payouts for
                  activity that violates these terms or our general Terms of Service.
                </li>
                <li>
                  You agree to represent ShoutOut honestly and in line with our brand &mdash; no
                  spam, no misleading claims about earnings, and no targeting of minors.
                </li>
                <li>
                  You are responsible for any taxes owed on payouts you receive as a partner.
                </li>
              </ul>
              <p className="text-xs text-gray-500">
                Agreement version v1. By clicking "Agree &amp; activate" you acknowledge you have
                read and accepted these terms and ShoutOut's general Terms of Service.
              </p>
            </div>

            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToPartnerTerms}
                onChange={(e) => setAgreedToPartnerTerms(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-gray-200">
                I have read and agree to the ShoutOut Partner Program terms.
              </span>
            </label>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowPartnerModal(false); setAgreedToPartnerTerms(false); }}
                className="px-4 py-2 rounded-xl bg-white/10 text-white text-sm hover:bg-white/15 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEnrollAsPartner}
                disabled={!agreedToPartnerTerms || enrollingPartner}
                className="px-4 py-2 rounded-xl bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enrollingPartner ? 'Activating…' : 'Agree & activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proof Upload Modal */}
      {showProofUpload && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/20 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Upload Bio Screenshots</h3>
              <button onClick={() => setShowProofUpload(false)} className="text-gray-400 hover:text-white">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              Upload screenshots of your profile on 2 platforms showing your ShoutOut Fans link in your bio.
              After admin approval, you'll receive a <span className="text-white font-semibold">$30 payout</span>.
            </p>

            <div className="flex items-center gap-2 mb-4">
              {[1, 2].map(n => (
                <div
                  key={n}
                  className={`flex-1 h-2 rounded-full ${
                    proofCount >= n ? 'bg-green-500' : 'bg-white/10'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-400 mb-4">{proofCount}/2 screenshots uploaded</p>

            {/* Follower count section */}
            <div className="mb-5">
              <p className="text-white text-sm font-semibold mb-1">Your follower count <span className="text-gray-500 font-normal">(optional)</span></p>
              <p className="text-gray-500 text-xs mb-3">Tell us how many followers you have on each platform where your link is live.</p>

              {/* Platform selector */}
              <div className="flex flex-wrap gap-2 mb-3">
                {SOCIAL_NETWORKS.map(n => {
                  const selected = selectedPlatforms.includes(n.id);
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        setSelectedPlatforms(prev =>
                          selected ? prev.filter(p => p !== n.id) : [...prev, n.id]
                        );
                        if (selected) {
                          setFollowerInputs(prev => { const u = { ...prev }; delete u[n.id]; return u; });
                        }
                      }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                        selected
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/30'
                      }`}
                    >
                      {n.label}
                    </button>
                  );
                })}
              </div>

              {selectedPlatforms.length > 0 && (
                <div className="space-y-2">
                  {selectedPlatforms.map(platformId => {
                    const network = SOCIAL_NETWORKS.find(n => n.id === platformId);
                    return (
                      <div key={platformId} className="flex items-center gap-2">
                        <span className="text-gray-400 text-xs w-24 flex-shrink-0">{network?.label}</span>
                        <input
                          type="number"
                          min={0}
                          placeholder="e.g. 12500"
                          value={followerInputs[platformId] || ''}
                          onChange={e => setFollowerInputs(prev => ({ ...prev, [platformId]: parseInt(e.target.value) || 0 }))}
                          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {proofCount < 2 && (
              <label className="block">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProofUpload}
                  className="hidden"
                  disabled={uploadingProof}
                />
                <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-white/20 rounded-xl text-white cursor-pointer hover:border-blue-500 hover:bg-blue-500/10 transition-colors">
                  <CameraIcon className="h-5 w-5" />
                  {uploadingProof ? 'Uploading...' : `Upload Screenshot ${proofCount + 1}`}
                </div>
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorHubDashboard;
